import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { PathName, getRouteByKey } from 'utility/routes';
import Persist, { StorageKind } from 'lib/persist';
import { RootState, useAppDispatch } from 'lib/centralStore';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ButtonRegistrationWrapper } from '../buttonRegistrationWrapper';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import { RadioButton } from 'components/inputs/radioButton';
import { RegistrationValue } from 'features/signup/types/signUpEnum';
import { RichText } from 'components/richText';
import { ToggleCheckBox } from 'components/inputs/toggleCheckBox';
import { gtag } from 'utility/functions';
import { resetAuthentication } from 'features/auth/authSlice';
import { setPrivacyConsents } from 'features/signup/signup';
import styles from 'components/page-content/registrati/registrati.module.scss';
import { useLazyActiveAccountQuery } from 'features/api/userSlice';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const localStorage = Persist(StorageKind.local);

export type LabelsPrivacyConsents = {
  titlePrivacyConsents: string;
  descriptionPrivacyConsents: string;
  contractTitle: string;
  profilingTitle: string;
  textFirstConsent: string;
  textSecondConsent: string;
  textThirdConsent: string;
  marketingTitle: string;
  acceptanceButton: Array<{ label: string; value: string }>;
  acceptAll: string;
  privacyTitle: string;
};

export type PrivacyConsentsProps = {
  next: Dispatch<SetStateAction<boolean>>;
  back: Dispatch<SetStateAction<boolean>>;
  labels: LabelsPrivacyConsents;
  handleRegistrationValue?: Dispatch<SetStateAction<RegistrationValue>>;
  isFinalStep?: boolean;
  labelLastStep?: string;
};

export const PrivacyConsents: React.FC<PrivacyConsentsProps> = ({
  next,
  back,
  labels,
  isFinalStep,
  labelLastStep,
  handleRegistrationValue,
}) => {
  const state = useSelector((store: RootState) => store.signup);
  const dispatch = useAppDispatch();
  const { asPath } = useRouter();
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);
  const [trigger] = useLazyActiveAccountQuery();
  const schema = z.object({
    acceptContract: z.boolean().refine((state) => state === true),
    acceptTermAndCondition: z.boolean().refine((state) => state === true),
    privacyInformation: z.boolean().refine((state) => state === true),
    profilingUsage: z.boolean(),
    marketingUsage: z.boolean(),
  });
  type SchemaValidation = z.infer<typeof schema>;
  const goNext: SubmitHandler<SchemaValidation> = async (data) => {
    dispatch(setPrivacyConsents(data));
    if (isFinalStep) {
      try {
        await trigger({ ...state, ...data }).unwrap();
        handleRegistrationValue?.(RegistrationValue.SUCCESS);
      } catch (error) {
        console.error(error);
        handleRegistrationValue?.(RegistrationValue.ERROR);
      } finally {
        dispatch(resetAuthentication());
      }
    } else {
      next(true);
    }
  };

  const { control, handleSubmit, setValue, formState } = useForm<SchemaValidation>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      acceptContract: state.acceptContract,
      acceptTermAndCondition: state.acceptTermAndCondition,
      marketingUsage: state.marketingUsage ?? undefined,
      privacyInformation: state.privacyInformation ?? undefined,
      profilingUsage: state.profilingUsage ?? undefined,
    },
  });

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);
      if (step !== 'privacy-consents') {
        localStorage.setItem(REGISTRATION_STEP_KEY, 'privacy-consents');
        gtag(
          {
            section: 'RegistrationStepPrivacyConsents',
            detail: 'RegistrationStepPrivacyConsents',
            event: 'RegistrationStepPrivacyConsents',
          },
          true
        );
      }
    }
  }, [registrationUrl, asPath]);

  const acceptAllHandle = () => {
    Object.keys(control._fields).forEach((key) => {
      setValue(key as keyof SchemaValidation, true);
      return control._fields;
    });
    goNext(control._formValues as SchemaValidation);
  };

  return (
    <div className={styles.formContainer}>
      <h2 className={styles.title}>{labels.titlePrivacyConsents}</h2>
      <RichText containerClassName={styles.subTitle}>{labels.descriptionPrivacyConsents}</RichText>
      <form onSubmit={handleSubmit(goNext)} className={`${styles.form} ${styles.formPrivacyConsent}`}>
        <h3 className={styles.contractTitle}>{labels.contractTitle}</h3>
        <ToggleCheckBox
          control={control}
          name={'acceptContract'}
          value={labels.textFirstConsent}
          hasMarginBottom
          errorMessage={formState.errors.acceptContract?.message}
        />

        <ToggleCheckBox
          value={labels.textSecondConsent}
          control={control}
          name="acceptTermAndCondition"
          errorMessage={formState.errors.acceptTermAndCondition?.message}
        />
        <hr className={styles.divider} />
        <h3 className={styles.contractTitle}>{labels.privacyTitle}</h3>
        <ToggleCheckBox
          control={control}
          name="privacyInformation"
          value={labels.textThirdConsent}
          hasMarginBottom={true}
          errorMessage={formState.errors.privacyInformation?.message}
        />
        <div className={styles.consentContainer}>
          <h4 className={styles.consentTitle}>{labels.profilingTitle}</h4>
          <RadioButton
            name="profilingUsage"
            options={labels.acceptanceButton.map((option) => ({
              label: option.label,
              value: JSON.parse(option.value), // CONVERT TO BOOLEAN
            }))}
            control={control}
          />
          {formState.errors.profilingUsage?.message && (
            <span className={styles.errorMessage}>{formState.errors.profilingUsage?.message}&nbsp;</span>
          )}
        </div>
        <div className={styles.consentContainer}>
          <h4 className={styles.consentTitle}>{labels.marketingTitle}</h4>
          <RadioButton
            name="marketingUsage"
            options={labels.acceptanceButton.map((option) => ({
              label: option.label,
              value: JSON.parse(option.value), // CONVERT TO BOOLEAN
            }))}
            control={control}
          />
          {formState.errors.marketingUsage?.message && (
            <span className={styles.errorMessage}>{formState.errors.marketingUsage?.message}&nbsp;</span>
          )}
        </div>
        <ButtonRegistrationWrapper handleGoBack={back!} isNextDisabled={!formState.isValid} lastStep={labelLastStep}>
          <button className={styles.acceptAll} type="button" onClick={acceptAllHandle}>
            {labels.acceptAll}
          </button>
        </ButtonRegistrationWrapper>
      </form>
    </div>
  );
};
